import { useState } from "react";
import { motion } from "framer-motion";
import { Menu, Instagram } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { LogoFull } from "./Logo";
import { NAV_LINKS, scrollToId } from "@/lib/content";
import { useExtras } from "@/lib/texts";

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const { instagram } = useExtras();

    const go = (id) => {
        setOpen(false);
        setTimeout(() => scrollToId(id), 60);
    };

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="fixed inset-x-0 top-0 z-50 border-b border-[rgba(231, 92, 21,0.2)] bg-[rgba(249,247,242,0.82)] backdrop-blur-md"
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
                <button
                    onClick={() => window.__lenis?.scrollTo(0)}
                    className="text-left"
                    aria-label="Torna all'inizio"
                >
                    <LogoFull />
                </button>

                <nav className="hidden items-center gap-8 lg:flex">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="group relative font-mono-alt text-xs uppercase tracking-[0.18em] text-stone-600 transition-colors hover:text-stone-900"
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#E75C15] transition-all duration-300 group-hover:w-full" />
                        </button>
                    ))}
                    <a
                        href={instagram}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="nav-instagram-link"
                        aria-label="Seguici su Instagram"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-900/15 text-stone-600 transition-colors duration-300 hover:border-[#E75C15] hover:text-[#E75C15]"
                    >
                        <Instagram className="h-4 w-4" />
                    </a>
                    <button
                        data-testid="nav-cta-consulenza"
                        onClick={() => go("contatti")}
                        className="rounded-full bg-[#1C1917] px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-stone-100 transition-colors duration-300 hover:bg-[#B8430D]"
                    >
                        Richiedi Consulenza
                    </button>
                </nav>

                <Sheet open={open} onOpenChange={setOpen}>
                    <SheetTrigger asChild>
                        <button
                            data-testid="nav-mobile-menu-button"
                            className="p-2 lg:hidden"
                            aria-label="Apri menu"
                        >
                            <Menu className="h-6 w-6 text-stone-900" />
                        </button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-full border-l-[#E75C15]/30 bg-[#1C1917] sm:max-w-sm">
                        <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
                        <div className="mt-10 flex flex-col gap-6">
                            {NAV_LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    data-testid={`nav-mobile-link-${l.id}`}
                                    initial={{ x: 40, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                    onClick={() => go(l.id)}
                                    className="text-left font-display text-3xl text-stone-200 transition-colors hover:text-[#E75C15]"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <button
                                data-testid="nav-mobile-cta-consulenza"
                                onClick={() => go("contatti")}
                                className="mt-6 rounded-full bg-[#E75C15] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[#1C1917]"
                            >
                                Richiedi Consulenza
                            </button>
                            <motion.a
                                href={instagram}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="nav-mobile-instagram-link"
                                initial={{ x: 40, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.08 * NAV_LINKS.length + 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                className="mt-4 inline-flex items-center gap-3 text-sm uppercase tracking-wider text-stone-400 transition-colors hover:text-[#E75C15]"
                            >
                                <Instagram className="h-5 w-5 text-[#E75C15]" />
                                Seguici su Instagram
                            </motion.a>
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </motion.header>
    );
};
